import React from 'react';

import { Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import { TablePagination } from '../../../components';
import { money } from '../../../utils';

import './styles.scoped.css';

const columnClassName = 'align-middle text-nowrap';

const moneyCell = instance => money.format(Number(instance.value) || 0);
// Coerce to Number so react-table sorts the money columns numerically regardless of whether
// the proc returns a string or a number.
const moneySort = (rowA, rowB, columnId) => Number(rowA.values[columnId]) - Number(rowB.values[columnId]);

// Accessors match the exact aliased columns returned by sp_cl_cp_bi_401kmc (spaces, not
// underscores), per the CP-803 spec. SSN is returned complete/unmasked for the audit — do not
// log the raw result set.
export const matchCalcColumns = [
  { Header: 'Employee SSN', accessor: 'Employee SSN', className: columnClassName },
  { Header: 'Employer Assigned ID', accessor: 'Employer Assigned ID', className: columnClassName },
  { Header: 'Last Name', accessor: 'Last Name', className: columnClassName },
  { Header: 'First Name', accessor: 'First Name', className: columnClassName },
  { Header: 'Legal Entity', accessor: 'Legal Entity', className: columnClassName },
  { Header: 'Company', accessor: 'Company', className: columnClassName },
  { Header: 'Most Recent Hire Date', accessor: 'Most Recent Hire Date', className: columnClassName },
  { Header: 'Termination Date', accessor: 'Termination Date', className: columnClassName },
  {
    Header: 'YTD 401K Deduction',
    accessor: 'YTD 401K Deduction',
    className: columnClassName,
    sortType: moneySort,
    Cell: moneyCell,
  },
  {
    Header: 'YTD Roth Contribution',
    accessor: 'YTD Roth Contribution',
    className: columnClassName,
    sortType: moneySort,
    Cell: moneyCell,
  },
  {
    Header: '401K YTD Sum',
    accessor: '401K YTD Sum',
    className: columnClassName,
    sortType: moneySort,
    Cell: moneyCell,
  },
  {
    Header: '401K Match',
    accessor: '401K Match',
    className: columnClassName,
    sortType: moneySort,
    Cell: moneyCell,
  },
  // Driver email for building the company-match notification distribution list (CP-838).
  // Plain text — accessor must match the proc alias sp_cl_cp_bi_401kmc … as [Driver Email].
  { Header: 'Driver Email', accessor: 'Driver Email', className: columnClassName },
];

function getSortedIcon(isSorted, isSortedDesc) {
  let iconProp = <FaSort />;
  if (isSorted) {
    iconProp = isSortedDesc ? <FaSortDown /> : <FaSortUp />;
  }
  return iconProp;
}

const MatchCalc401KTable = ({ data, loading }) => {
  const tableData = Array.isArray(data) ? data : [];
  const tableInstance = useTable(
    {
      columns: matchCalcColumns,
      data: tableData,
      initialState: {
        pageSize: 200,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => (
      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
        {column.render('Header')}
        <span>{column.canSort ? getSortedIcon(column.isSorted, column.isSortedDesc) : null}</span>
      </th>
    ));
    return <tr {...headerGroup.getHeaderGroupProps()}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => (
      <td
        {...cell.getCellProps({
          className: cell.column.className,
          style: cell.column.style,
        })}>
        {cell.render('Cell')}
      </td>
    ));

    return <tr {...row.getRowProps()}>{cells}</tr>;
  });

  return (
    <Container fluid>
      <Row className="align-items-center">
        <Col className="d-flex justify-content-center justify-content-md-end">
          <TablePagination tableInstance={tableInstance} />
        </Col>
      </Row>
      <Row>
        <Col className="border border-light rounded shadow-sm">
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            {loading && <Spinner animation="border" />}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default MatchCalc401KTable;
